import {useConfig} from '@aboutyou/config';
import {
  useActivateApiAbTestCallback,
  useVariantActiveForAbTestBackend,
} from '@aboutyou/feature-flags';
import {ProductTilePreferences} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product_tile_preferences.types';
import {SpotlightSection} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/sections.types';
import {
  GetBrowseSectionsNextResponse,
  GetBrowseSectionsResponse,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/navigation_page/v1/service.types';
import {useBrandsOverviewPage, useFavouritesPage} from '@aboutyou/router';
import {InView} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {ComponentType, memo} from 'react';
import {DynamicAbTestActivationTrigger} from '../../../client-services/DynamicAbTestActivation/DynamicAbTestActivationTrigger';
import {HeroBrandsSlider} from '../../../features/HubPage/Sections/HeroBrandsSlider/HeroBrandsSlider';
import {CustomComponents} from '../../../features/HubPage/types';
import {HomePageGridAreaNames} from '../utils/gridAreaNames';
import {BodyTypesSection} from './BodyTypesSection';
import {CategoryHighlightSection} from './CategoryHighlightSection/CategoryHighlightSection';
import {InspirationGridSection} from './InspirationGridSection/InspirationGridSection';
import {ProductsSection} from './ProductsSection';
import {StorySection} from './StorySection/StorySection';
import {TargetGroup} from './TargetGroup';
import {mapBrandElement} from './mapBrandElementToHeroBrands';

type itemsType = Array<
  GetBrowseSectionsResponse | GetBrowseSectionsNextResponse
>;

interface StreamComponentListProps
  extends Omit<CustomComponents, 'SliderComponent' | 'StreamTileList'> {
  items: itemsType;
  gridColumns: number | undefined;
  itemsPreferences: ProductTilePreferences | undefined;
  SliderComponent: ComponentType<SpotlightSection & {isFetching?: boolean}>;
  onAddToBasketCtaClick?: ({
    productId,
    couponPromotionId,
  }: {
    productId: number;
    couponPromotionId?: string;
  }) => void;
  isFetching?: boolean;
}

const StreamComponentList: React.FC<StreamComponentListProps> = ({
  items,
  SliderComponent,
  onAddToBasketCtaClick,
  isFetching,
}) => {
  const {device} = useConfig();
  const activateApiAbTest = useActivateApiAbTestCallback();
  const isVariantActiveEntryPrice = useVariantActiveForAbTestBackend({
    experimentKey: 'ab_entry_price_reco',
  });
  const brandsOverviewPage = useBrandsOverviewPage();
  const favouritesPage = useFavouritesPage();

  return (
    <>
      {items.map((item, index) => {
        switch (item.section.type?.$case) {
          case 'targetGroup': {
            if (device === 'desktop') {
              return null;
            }

            return (
              <TargetGroupGridAreaWrapper
                data-without-gap
                key={`browse-targetGroup-${index}`}
              >
                <TargetGroup {...item.section.type.targetGroup} />
              </TargetGroupGridAreaWrapper>
            );
          }
          case 'spotlight':
            return (
              <SpotlightGridAreaWrapper
                data-without-gap
                key={`browse-spotlight-${index}`}
              >
                <SliderComponent
                  elements={item.section.type.spotlight.elements}
                  isFetching={isFetching}
                />
              </SpotlightGridAreaWrapper>
            );
          case 'recommendedBrands': {
            const {topline, headline, elements} =
              item.section.type.recommendedBrands;
            return (
              <HeroBrandsSlider
                key={`recommended-brands-${index}`}
                applyCmsBackgroundColor
                trackProductListSwipeEvent
                streamPosition={index}
                onAddToBasketCtaClick={onAddToBasketCtaClick}
                allBrandsUrl={brandsOverviewPage}
                trackComponentConfig={{
                  location_id: 'recommendedBrands',
                }}
                topline={topline}
                headline={headline}
                elements={elements.map(mapBrandElement)}
              />
            );
          }
          case 'popularBrands': {
            const {topline, headline, elements} =
              item.section.type.popularBrands;
            return (
              <HeroBrandsSlider
                key={`popular-brands-${index}`}
                trackProductListSwipeEvent
                streamPosition={index}
                onAddToBasketCtaClick={onAddToBasketCtaClick}
                allBrandsUrl={brandsOverviewPage}
                trackComponentConfig={{
                  location_id: 'popularBrands',
                }}
                topline={topline}
                headline={headline}
                elements={elements.map(mapBrandElement)}
              />
            );
          }
          case 'suggestedBrands': {
            const {headline, elements, subline} =
              item.section.type.suggestedBrands;
            return (
              <HeroBrandsSlider
                key={`suggested-brands-${index}`}
                trackProductListSwipeEvent
                streamPosition={index}
                onAddToBasketCtaClick={onAddToBasketCtaClick}
                allBrandsUrl={brandsOverviewPage}
                topline={subline}
                headline={headline}
                elements={elements.map(mapBrandElement)}
                trackComponentConfig={{
                  location_id: 'suggestedBrands',
                }}
              />
            );
          }
          case 'favouriteBrands': {
            const {headline, elements, subline} =
              item.section.type.favouriteBrands;
            return (
              <HeroBrandsSlider
                key={`favourite-brands-${index}`}
                trackProductListSwipeEvent
                streamPosition={index}
                onAddToBasketCtaClick={onAddToBasketCtaClick}
                topline={subline}
                headline={headline}
                elements={elements.map(mapBrandElement)}
                allBrandsUrl={favouritesPage}
                trackComponentConfig={{
                  location_id: 'favouriteBrands',
                }}
              />
            );
          }
          case 'products':
            {
              const product = item.section.type.products;
              const isSalesPicksLane =
                product.promotionTracker.locationId?.includes(
                  'salesPickProducts',
                );
              return (
                <DynamicAbTestActivationTrigger
                  key={`products-${index}`}
                  experimentKey={
                    isSalesPicksLane ? 'ab_csi_reco_slp_' : undefined
                  }
                >
                  <InView
                    onChange={inView => {
                      if (
                        inView &&
                        isVariantActiveEntryPrice &&
                        isSalesPicksLane
                      ) {
                        activateApiAbTest('ab_entry_price_reco', {
                          triggerMultipleTimesPerSession: true,
                        });
                      }
                    }}
                    threshold={0.8}
                    triggerOnce
                  >
                    <ProductsSection
                      streamPosition={index}
                      onAddToBasketCtaClick={onAddToBasketCtaClick}
                      trackComponentConfig={{
                        location_id:
                          product.promotionTracker.locationId ?? 'products',
                      }}
                      {...item.section.type.products}
                    />
                  </InView>
                </DynamicAbTestActivationTrigger>
              );
            }
            break;
          case 'categoryHighlight':
            return (
              <CategoryHighlightSection
                key={`categories-${index}`}
                streamPosition={index}
                {...item.section.type.categoryHighlight}
              />
            );
          case 'story': {
            return (
              <StorySection
                key={`story-${index}`}
                streamPosition={index}
                {...item.section.type.story}
              />
            );
          }
          // TODO: Temporarily hidden because the "Discover more" CTA leads to an empty page. Needs further iteration. https://aboutyou.atlassian.net/browse/ASFE-13311
          // case 'fashionUpdateTeaser':
          // return (
          //   <FashionUpdate
          //     key={`fashion-update-teaser-${index}`}
          //     streamPosition={index}
          //     onAddToBasketCtaClick={onAddToBasketCtaClick}
          //       trackComponentConfig={{
          //         location_id: 'fashionUpdateTeaser',
          //       }}
          //     {...item.section.type.fashionUpdateTeaser}
          //   />
          // );
          case 'bodyTypes':
            return (
              <BodyTypesSection
                key={`body-types-${index}`}
                streamPosition={index}
                {...item.section.type.bodyTypes}
              />
            );
          case 'inspirationGrid':
            return (
              <InspirationGridSection
                key={`inspiration-grid-${index}`}
                streamPosition={index}
                {...item.section.type.inspirationGrid}
              />
            );
          case 'celebrityCollections': {
            const {headline, elements, subline} =
              item.section.type.celebrityCollections;
            return (
              <HeroBrandsSlider
                key={`celebrity-collections-${index}`}
                trackProductListSwipeEvent
                streamPosition={index}
                onAddToBasketCtaClick={onAddToBasketCtaClick}
                topline={subline}
                headline={headline}
                elements={elements.map(mapBrandElement)}
                trackComponentConfig={{
                  location_id: 'celebrityCollections',
                }}
              />
            );
          }
          default:
            return null;
        }
      })}
    </>
  );
};

const TargetGroupGridAreaWrapper = styled.div`
  grid-area: ${HomePageGridAreaNames.TARGET_GROUP};
`;

const SpotlightGridAreaWrapper = styled.div`
  grid-area: ${HomePageGridAreaNames.SPOTLIGHT};
`;

const StreamComponentListMemo = memo(StreamComponentList);
export {StreamComponentListMemo as StreamComponentList};
